'use client'

import { useState, useEffect, useContext } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useSearch } from '@/src/contexts/SearchContext'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'
import { useApi } from '@/src/hooks'
import { useCurrentLocation } from '../../hooks/useCurrentLocation'

import { Button } from 'antd'
import { SearchBarCategories } from './SearchBarCategories'
import { MultiSearchBar } from './MultiSearchBar'
import { LocationSearchBar } from './LocationSearchBar'

import { parseLocationString } from '@/src/utils'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'

import {
  DISALLOWED_LOCATION_CHARS,
  DEFAULT_DISTANCE,
  DEFAULT_US_ADDRESS,
  DEFAULT_US_COORDINATES,
  VALIDATION_MESSAGES,
} from '@/src/data'
import { validateLocationInput, validateMainSearchInput } from '@/src/utils'
import { QueryStringParams } from '@/src/interfaces'

import { trackSearchEvents, trackValidationEvents } from '@/src/lib'

interface MainSearchBarProps {
  searchParams: QueryStringParams
  setSearchParams: (params: QueryStringParams) => void
}

interface OptionType {
  label: string
  value: string
  key: string
  category?: string
  sub_category?: string
  group?: string
  highlightedCategory?: string
  highlightedSubCategory?: string
}

export const MainSearchBar = ({
  searchParams,
  setSearchParams,
}: MainSearchBarProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const isSearchRoute = pathname === '/search'
  const {
    query,
    setQuery,
    currentSearchBarCategory,
    shouldClearSearch,
    setShouldClearSearch,
    locationError,
    setLocationError,
    addressData,
    setAddressData,
  } = useSearch()
  const {
    getCoordinatesFromAddress,
    fetchLocation,
    geoLocation,
    geoLocationError,
    loadingGeoLocation,
    clearLocation,
    isUS,
  } = useCurrentLocation()
  const [localAutocompleteValue, setLocalAutocompleteValue] = useState('')
  const [localLocationValue, setLocalLocationValue] = useState('')
  const [physicianError, setPhysicianError] = useState<string | null>(null)
  const [, setIsPhysicianValid] = useState(false)
  const [, setIsLocationValid] = useState(false)
  const [isNavigatingDropdown, setIsNavigatingDropdown] = useState(false)
  const [isNavigatingPhysicianDropdown, setIsNavigatingPhysicianDropdown] =
    useState(false)
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  const {
    autocompleteQuery,
    setAutocompleteQuery,
    setLocationAutocompleteQuery,
    options,
    spellSuggestion,
    setSpellSuggestion,
    locationOptions,
    fetchCategoryAutoSelection,
  } = useApi(currentSearchBarCategory)

  const [isSpellSuggestionClick, setIsSpellSuggestionClick] = useState(false)
  const [isSelectingFromDropdown, setIsSelectingFromDropdown] = useState(false)

  const initializeValues = () => {
    if (query.singleValueParameters?.value) {
      setLocalAutocompleteValue(query.singleValueParameters.value)
    }
    if (
      query.locationParameters?.city ||
      query.locationParameters?.state ||
      query.locationParameters?.zip
    ) {
      const location = [
        query.locationParameters.city,
        query.locationParameters.state,
        query.locationParameters.zip,
      ]
        .filter(Boolean)
        .join(', ')
      setLocalLocationValue(location)
    }
  }

  useEffect(() => {
    initializeValues()
  }, [query.locationParameters])

  useEffect(() => {
    if (shouldClearSearch) {
      // Clear all location-related values
      setLocalLocationValue('')
      setLocationError(null)
      setIsLocationValid(true)
      setLocationAutocompleteQuery('')
      setAddressData(null)
      setShouldClearSearch(false)
    }
  }, [shouldClearSearch, setShouldClearSearch])

  const clearAllValues = () => {
    setLocalAutocompleteValue('')
    setLocalLocationValue('')
    setPhysicianError(null)
    setLocationError(null)
    setIsPhysicianValid(false)
    setIsLocationValid(true)
    setAutocompleteQuery({ key: currentSearchBarCategory, value: '' })
    setLocationAutocompleteQuery('')
    setSpellSuggestion('')
    setAddressData(null)
  }

  // Add a new useEffect to watch for query resets when clicking the New Search button
  useEffect(() => {
    // Check if the query has been reset (all parameters are empty/default)
    const isQueryReset =
      !query.singleValueParameters?.value &&
      !query.locationParameters?.city &&
      !query.locationParameters?.state &&
      !query.locationParameters?.zip &&
      !query.geoDistanceParameters?.currentLocation?.lat &&
      !query.geoDistanceParameters?.currentLocation?.lon

    if (isQueryReset) {
      clearAllValues()
    }
  }, [query])

  const handleAutocompleteChange = (value: string) => {
    setLocalAutocompleteValue(value)
    setAutocompleteQuery({
      key: currentSearchBarCategory,
      value,
    })

    if (!value) {
      setPhysicianError(null)
      setIsPhysicianValid(false)
      setSpellSuggestion('')
      return
    }

    // Don't validate or update query for single character input
    if (value.length === 1) {
      return
    }

    const validation = validateMainSearchInput(value, currentSearchBarCategory)

    setPhysicianError(validation.errorMessage || null)
    setIsPhysicianValid(validation.isValid)

    // Track validation error if present
    if (validation.errorMessage) {
      trackValidationEvents.searchError(trackAmplitudeEvent)({
        'Error Message': validation.errorMessage,
        'Input Value': value,
        'Input Type': currentSearchBarCategory,
      })
    }
  }

  const handleLocationChange = async (value: string) => {
    setLocalLocationValue(value)
    setLocationAutocompleteQuery(value)

    // Clear addressData when user starts typing
    if (addressData?.fullAddress) {
      setAddressData(null)
    }

    if (!value) {
      setLocationError(null)
      setIsLocationValid(true)
      setQuery((prev) => ({
        ...prev,
        locationParameters: { state: '', city: '', zip: '' },
        geoDistanceParameters: {
          ...prev.geoDistanceParameters,
          currentLocation: { lat: 0, lon: 0 },
        },
      }))
      return
    }

    // Only check for invalid characters while typing
    const hasInvalidChars = value
      .split(',')
      .some((part) => DISALLOWED_LOCATION_CHARS.test(part))

    if (hasInvalidChars) {
      const errorMessage = VALIDATION_MESSAGES.LOCATION.INVALID_CHARS
      setLocationError(errorMessage)
      setIsLocationValid(false)

      return
    }

    // Clear any existing errors while typing
    setLocationError(null)
    setIsLocationValid(true)

    // Only try to get coordinates if the input looks like a complete location
    // (has at least city and state, or is a zip code)
    const parts = value.split(',').map((part) => part.trim())
    const isZipCode = /^\d{5}(-\d{4})?$/.test(parts[0])
    const hasCityAndState = parts.length >= 2 && parts[0] && parts[1]

    if (isZipCode || hasCityAndState) {
      try {
        const coordinates = await getCoordinatesFromAddress(value)
        if (coordinates) {
          const { city, state, zip } = parseLocationString(value)
          setQuery((prev) => ({
            ...prev,
            locationParameters: {
              city,
              state,
              zip,
            },
            geoDistanceParameters: {
              ...prev.geoDistanceParameters,
              currentLocation: {
                lat: coordinates.lat,
                lon: coordinates.lon,
              },
            },
          }))
        }
      } catch (error) {
        // Don't show error while typing, just log it
        console.error('Error getting coordinates:', error)
      }
    }
  }

  const handleSearchClick = async (isAutoSelection: boolean = false) => {
    try {
      // Prevent multiple simultaneous searches
      if (loadingGeoLocation) return

      let newLocalAutocompleteValue = localAutocompleteValue
      let newCategory = undefined

      // Show validation error for single character input when search is triggered
      if (newLocalAutocompleteValue && newLocalAutocompleteValue.length === 1) {
        const errorMessage =
          currentSearchBarCategory === 'full_name'
            ? VALIDATION_MESSAGES.PHYSICIAN.full_name
            : VALIDATION_MESSAGES.PHYSICIAN.unified
        setPhysicianError(errorMessage)
        setIsPhysicianValid(false)
        trackValidationEvents.searchError(trackAmplitudeEvent)({
          'Error Message': errorMessage,
          'Input Value': newLocalAutocompleteValue,
          'Input Type': currentSearchBarCategory,
        })
        return
      }

      if (isAutoSelection && autocompleteQuery.key === 'unified') {
        let possibleCategory = await fetchCategoryAutoSelection(
          localAutocompleteValue
        )
        newCategory = possibleCategory
        setIsNavigatingDropdown(false)
        newLocalAutocompleteValue = possibleCategory.value
        setLocalAutocompleteValue(newLocalAutocompleteValue)
        setAutocompleteQuery(possibleCategory)
      }

      // Validate MultiSearchBar input when search is triggered
      if (newLocalAutocompleteValue) {
        const validation = validateMainSearchInput(
          newLocalAutocompleteValue,
          currentSearchBarCategory
        )
        if (!validation.isValid) {
          setPhysicianError(validation.errorMessage || null)
          setIsPhysicianValid(false)
          if (validation.errorMessage) {
            trackValidationEvents.searchError(trackAmplitudeEvent)({
              'Error Message': validation.errorMessage,
              'Input Value': newLocalAutocompleteValue,
              'Input Type': currentSearchBarCategory,
            })
          }
          return
        }
      }

      // Validate location only when search is triggered
      if (
        localLocationValue &&
        !isNavigatingDropdown &&
        !isSelectingFromDropdown
      ) {
        const validation = validateLocationInput(localLocationValue)
        if (!validation.isValid) {
          setLocationError(validation.errorMessage || null)
          setIsLocationValid(false)
          if (validation.errorMessage) {
            trackValidationEvents.locationError(trackAmplitudeEvent)({
              'Error Message': validation.errorMessage,
              'Input Value': localLocationValue,
              'Input Type': 'location',
            })
          }
          return
        }

        const parts = localLocationValue
          .trim()
          .split(',')
          .map((part) => part.trim())
        const endsWithComma = localLocationValue.trim().endsWith(',')
        const hasOnlyCity =
          parts.length === 1 && !localLocationValue.includes(',')
        const hasIncompleteState =
          parts.length === 2 && (!parts[1] || parts[1].length === 0)
        const isZipCode = /^\d{5}(-\d{4})?$/.test(parts[0])

        if (
          (endsWithComma || hasOnlyCity || hasIncompleteState) &&
          !isZipCode
        ) {
          const errorMessage = VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT
          setLocationError(errorMessage)
          setIsLocationValid(false)

          trackValidationEvents.locationError(trackAmplitudeEvent)({
            'Error Message': errorMessage,
            'Input Value': localLocationValue,
            'Input Type': 'location',
          })
          return
        }
      }

      // Amplitude - Track the search initiated event
      trackSearchEvents.searchInitiated(trackAmplitudeEvent)({
        'Search Bar Category': currentSearchBarCategory,
        'Local Autocomplete Value': newLocalAutocompleteValue,
        'Local Location Value': localLocationValue,
        'Address Data': addressData,
        Options: options || [],
        'Location Options': locationOptions || [],
        'Is Navigating Dropdown': isNavigatingDropdown,
      })

      // Get coordinates for the location if it's manually typed
      let coordinates = null
      if (localLocationValue && !addressData) {
        try {
          coordinates = await getCoordinatesFromAddress(localLocationValue)
        } catch (error) {
          console.error('Error getting coordinates:', error)
          setLocationError(VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT)
          return
        }
      }

      // Update query with current values, preserving existing parameters
      setQuery((prevQuery) => {
        // If we have addressData, use it for location parameters
        const locationParams = addressData
          ? {
              city: addressData.city,
              state: addressData.state,
              zip: addressData.zip,
            }
          : prevQuery.locationParameters // Preserve initial location parameters if no new addressData

        // If we have geoLocation or coordinates from manual input, use it for currentLocation
        const currentLocation = geoLocation
          ? {
              lat: geoLocation.latitude,
              lon: geoLocation.longitude,
            }
          : coordinates
          ? {
              lat: coordinates.lat,
              lon: coordinates.lon,
            }
          : prevQuery.geoDistanceParameters?.currentLocation

        // Handle single value parameters - either from dropdown or manual input
        const finalSingleValueParameters = {
          key: newCategory?.key || currentSearchBarCategory,
          value: newLocalAutocompleteValue || autocompleteQuery.value,
        }

        // Handle location parameters - either from geolocation, dropdown, or manual input
        let finalLocationParams = locationParams
        if (localLocationValue && !addressData) {
          const parts = localLocationValue.split(',').map((part) => part.trim())
          if (parts.length >= 2) {
            const [city, state] = parts
            if (city && state) {
              finalLocationParams = {
                city,
                state,
                zip: parts[2] || '',
              }
            }
          }
        }

        return {
          ...prevQuery,
          singleValueParameters: finalSingleValueParameters,
          locationParameters: finalLocationParams,
          insuranceParameters: prevQuery.insuranceParameters,
          checkBoxedParameters: prevQuery.checkBoxedParameters,
          geoDistanceParameters: {
            ...prevQuery.geoDistanceParameters,
            currentLocation,
          },
          genderParameters: prevQuery.genderParameters,
        }
      })

      if (!isSearchRoute) {
        router.push('/search')
      }
    } catch (error) {
      console.error('Error during search:', error)
      setLocationError('An error occurred while processing your search')
    }
  }

  const clearAutocompleteValue = () => {
    if (localAutocompleteValue !== '') {
      setLocalAutocompleteValue('')
      setAutocompleteQuery({
        key: currentSearchBarCategory,
        value: '',
      })
      setQuery((prevQuery) => ({
        ...prevQuery,
        singleValueParameters: { key: currentSearchBarCategory, value: '' },
      }))
      setPhysicianError(null)
      setIsPhysicianValid(false)
      setSpellSuggestion('')
    }
  }

  const clearLocalAutocompleteValue = () => {
    if (localAutocompleteValue !== '') {
      setLocalAutocompleteValue('')
      setPhysicianError(null)
      setIsPhysicianValid(false)
    }
  }

  const formattedSuggestion = spellSuggestion
    ?.split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  const handleSuggestionClick = () => {
    if (!spellSuggestion) return
    if (spellSuggestion) {
      setLocalAutocompleteValue(spellSuggestion)
      setAutocompleteQuery({
        key: currentSearchBarCategory,
        value: spellSuggestion,
      })
      setSpellSuggestion('')
      setIsSpellSuggestionClick(true)
    }
  }

  const handleClearLocation = () => {
    setLocalLocationValue('')
    setLocationError(null)
    setIsLocationValid(true)
    clearLocation()
    setLocationAutocompleteQuery('')
    setAddressData(null)
    setQuery((prev) => ({
      ...prev,
      locationParameters: { state: '', city: '', zip: '' },
      geoDistanceParameters: {
        ...prev.geoDistanceParameters,
        currentLocation: { lat: 0, lon: 0 },
      },
    }))
  }

  useEffect(() => {
    if (isSpellSuggestionClick && localAutocompleteValue) {
      handleSearchClick()
      setIsSpellSuggestionClick(false)
    }
  }, [localAutocompleteValue, isSpellSuggestionClick])

  useEffect(() => {
    clearLocalAutocompleteValue()
  }, [currentSearchBarCategory])

  useEffect(() => {
    if (addressData) {
      if (isUS) {
        // Set the detected US location
        setQuery((prevQuery) => ({
          ...prevQuery,
          locationParameters: {
            state: addressData.state,
            city: addressData.city,
            zip: addressData.zip,
          },
          geoDistanceParameters: {
            distance:
              prevQuery.geoDistanceParameters?.distance || DEFAULT_DISTANCE,
            currentLocation: {
              lat: geoLocation?.latitude || 0,
              lon: geoLocation?.longitude || 0,
            },
          },
        }))

        // Update the location input value
        setLocalLocationValue(
          `${addressData.city}, ${addressData.state}${
            addressData.zip ? `, ${addressData.zip}` : ''
          }`
        )
      } else {
        // Set default New York location for non-US locations
        setQuery((prevQuery) => ({
          ...prevQuery,
          locationParameters: DEFAULT_US_ADDRESS,
          geoDistanceParameters: {
            distance: DEFAULT_DISTANCE,
            currentLocation: DEFAULT_US_COORDINATES,
          },
        }))
        setLocationError('Non-US location detected. Using default US location.')
      }
    }
  }, [addressData, geoLocation])

  useEffect(() => {
    const newLocalAutocompleteValue = query.singleValueParameters
    if (newLocalAutocompleteValue.key && newLocalAutocompleteValue.value) {
      setAutocompleteQuery({
        key: newLocalAutocompleteValue.key,
        value: newLocalAutocompleteValue.value,
      })
    }
  }, [])

  return (
    <div
      data-testid='main-search-bar'
      className='main-search-bar w-full flex flex-col'
      data-route={isSearchRoute ? 'search' : 'home'}
    >
      <SearchBarCategories
        setAutocompleteQuery={setAutocompleteQuery}
        setPhysicianError={setPhysicianError}
      />
      <div className='search-container flex flex-col w-full md:flex-row md:items-start lg:flex-col xl:flex-row'>
        {/* PHYSICIAN SPECIALTY, PHYSICIAN NAME, CONDITION SEARCH BAR */}
        <div className='search-input-container w-full'>
          <MultiSearchBar
            value={localAutocompleteValue}
            onChange={handleAutocompleteChange}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                setIsNavigatingPhysicianDropdown(true)
                e.preventDefault()
              } else if (e.key === 'Enter') {
                if (isNavigatingPhysicianDropdown && options?.length) {
                  e.preventDefault()
                } else {
                  // Amplitude - Track the search input updated event
                  trackSearchEvents
                    .searchInputUpdated(trackAmplitudeEvent)
                    .forSearch({
                      'Current Search Bar Category': currentSearchBarCategory,
                      'Input Value': localAutocompleteValue,
                      'Dropdown Option Selected': options
                        ? options.length > 0
                        : false,
                    })
                  handleSearchClick(true)
                }
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                setIsNavigatingPhysicianDropdown(false)
              }, 200)
            }}
            onSelect={(value: string, option?: OptionType) => {
              setIsNavigatingPhysicianDropdown(false)
              setPhysicianError(null)
              setLocalAutocompleteValue(value)
              setAutocompleteQuery({
                key: option?.group || currentSearchBarCategory,
                value,
              })

              // Amplitude - Track the search dropdown selected event
              trackSearchEvents.searchDropdownSelected(trackAmplitudeEvent)({
                'Input Type': currentSearchBarCategory,
                'Selected Value': value,
                'Previous Value': localAutocompleteValue,
              })

              setQuery({
                ...query,
                singleValueParameters: {
                  key: option?.group ? option.group : currentSearchBarCategory,
                  value,
                },
              })
            }}
            onClear={() => {
              clearAutocompleteValue()
              setSpellSuggestion('')
              setPhysicianError(null)
            }}
            options={options}
            searchCategory={currentSearchBarCategory}
          />
        </div>

        {/* LOCATION AND SEARCH BUTTON CONTAINER */}
        <div className='location-button-container flex flex-col md:flex-row lg:flex-col xl:flex-row w-full'>
          <div className='w-full'>
            <LocationSearchBar
              value={localLocationValue}
              onChange={handleLocationChange}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  setIsNavigatingDropdown(true)
                  e.preventDefault()
                } else if (e.key === 'Enter') {
                  if (isNavigatingDropdown && locationOptions?.length) {
                    e.preventDefault()
                  } else {
                    // Track the search input updated event
                    trackSearchEvents
                      .searchInputUpdated(trackAmplitudeEvent)
                      .forLocation({
                        'Input Value': localLocationValue,
                        'Dropdown Option Selected': locationOptions
                          ? locationOptions.length > 0
                          : false,
                      })
                    // Always trigger search on Enter
                    handleSearchClick()
                  }
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  setIsNavigatingDropdown(false)
                }, 200)
              }}
              onSelect={async (value) => {
                setIsNavigatingDropdown(false)
                setLocationError(null)
                setLocalLocationValue(value)
                setIsSelectingFromDropdown(true)

                try {
                  const coordinates = await getCoordinatesFromAddress(value)

                  if (coordinates) {
                    const { city, state, zip } = parseLocationString(value)

                    if (setSearchParams && searchParams) {
                      setSearchParams({
                        ...searchParams,
                        sortOrder: 'asc',
                        sortBy: 'distance',
                      })
                    }

                    setQuery((prev) => ({
                      ...prev,
                      locationParameters: {
                        city,
                        state,
                        zip,
                      },
                      geoDistanceParameters: {
                        ...prev.geoDistanceParameters,
                        currentLocation: {
                          lat: coordinates.lat,
                          lon: coordinates.lon,
                        },
                        distance:
                          prev.geoDistanceParameters?.distance ||
                          DEFAULT_DISTANCE,
                      },
                    }))

                    // Amplitude - Track the dropdown selection
                    trackSearchEvents.searchDropdownSelected(
                      trackAmplitudeEvent
                    )({
                      'Input Type': 'location',
                      'Selected Value': value,
                      'Previous Value': localLocationValue,
                    })

                    if (!isSearchRoute) {
                      router.push('/search')
                    }
                  } else {
                    setLocationError(
                      VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT
                    )
                  }
                } catch (error) {
                  console.error('Error getting coordinates:', error)
                  setLocationError(VALIDATION_MESSAGES.LOCATION.INVALID_FORMAT)
                } finally {
                  setIsSelectingFromDropdown(false)
                }
              }}
              onClear={handleClearLocation}
              options={locationOptions || []}
              loadingGeoLocation={loadingGeoLocation}
              hasCurrentLocation={!!addressData?.fullAddress}
              geoLocation={geoLocation}
              geoLocationError={geoLocationError}
              addressData={addressData}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              fetchLocation={fetchLocation}
            />
          </div>
          <Button
            type='primary'
            className='custom-button flex items-center justify-center w-full md:w-auto'
            data-testid='search-button'
            aria-label='Perform search'
            onClick={() => handleSearchClick(true)}
          >
            <FontAwesomeIcon
              icon='magnifying-glass'
              className='text-xl text-white'
            />
          </Button>
        </div>
      </div>
      {(physicianError || locationError) && (
        <div
          data-testid={physicianError ? 'physician-error' : 'location-error'}
          className='text-red-500 text-sm mt-2'
        >
          {physicianError || locationError}
        </div>
      )}
      {spellSuggestion && autocompleteQuery.key !== 'full_name' && (
        <div
          data-testid='spellcheck-info'
          className='mt-2 text-primary font-medium'
        >
          Did you mean:{' '}
          <button
            className='text-blue-500 hover:text-blue-800 italic font-bold focus:outline-none'
            onClick={handleSuggestionClick}
          >
            {formattedSuggestion}
          </button>
        </div>
      )}
    </div>
  )
}
